import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SDueDate = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1120-S Due Date | S Corporation Filing Deadlines & Penalties"
          description="Learn the 2025 IRS Form 1120-S due dates, extension rules, penalty details, and tips to avoid late filings for S corporations on calendar and fiscal years. "
          keywords=""
          canonical="resources/business-tax/form-1120-s-filing-deadlines-and-penalties-guide/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          IRS{' '}
          <span className="highlight-container">
            <span className="highlight">Form 1120-S</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-File your IRS <span style={{ fontWeight: 600 }}> Form 1120-S</span>{' '}
          for the 2025 tax year by{' '}
          <span style={{ fontWeight: 600 }}>March 16, 2026,</span> to meet
          federal S
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          corporation filing deadlines and avoid potential penalties.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sduedates`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  component="section"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-1120-s-filing-deadlines-and-penalties-guide/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>{' '}
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form1120-S Due Date at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1120-S filing deadlines
                        </strong>{' '}
                        for both calendar-year and fiscal-year S corporations,
                        including extension deadlines.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Late filing and payment penalties,
                        </strong>{' '}
                        common filing requirements, and how to avoid costly IRS
                        penalties.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Best practices for timely filing,
                        </strong>{' '}
                        along with answers to frequently asked questions to help
                        your S corporation stay compliant.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Staying compliant with the IRS requires more than just
                      filling out forms - it requires{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        filing them on time
                      </span>
                      . For S corporations, the annual return is filed using{' '}
                      <Link
                        href="/business-tax/efile-irs-form-1120-s/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1120-S
                      </Link>
                      , and its due date is much earlier than many business
                      owners expect.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Missing the deadline doesn’t just bring financial
                      penalties; it can also create headaches for your
                      shareholders, who rely on timely Schedule K-1s to complete
                      their personal tax filings. That’s why understanding the{' '}
                      <span style={{ fontWeight: 600 }}>
                        1120-S due date for 2026
                      </span>{' '}
                      is critical.
                    </Typography>
                  </Box>
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  /> */}
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When is Form 1120-S Due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why the 1120-S Deadline Exists
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#exten" sx={{ color: '#0202ff' }}>
                          Extension Due Date
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalties" sx={{ color: '#0202ff' }}>
                          Penalties for Late Filing or Payment
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#state" sx={{ color: '#0202ff' }}>
                          State Filing Deadlines
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#best" sx={{ color: '#0202ff' }}>
                          Best Practices to Avoid Missing the Deadline
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions (FAQs)
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                    scrollMarginTop: 83,
                  }}
                  id="when"
                >
                  When is Form 1120-S Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The following table lists the standard due dates for{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1120-S
                  </Link>
                  . Calendar-year filers have a deadline in March that never
                  changes. Fiscal-year filers, however, must file by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    15th day of the 3rd month
                  </span>{' '}
                  after the close of their tax year.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            S Corporation Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Tax Year End
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 1120-S Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Calendar-Year S Corporation
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            December 31, 2025
                          </TableCell>

                          <TableCell> March 16, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Fiscal-Year S Corporation
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            Last day of fiscal year
                          </TableCell>

                          <TableCell>
                            {' '}
                            15th day of the 3rd month after the tax year ends
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note: </b> If the filing deadline falls on a Saturday,
                  Sunday, or federal holiday, it automatically shifts to the
                  next business day. For 2026, March 15 falls on a Sunday, so
                  the deadline moves to March 16, 2026.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why the 1120-S Deadline Exists
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS sets an early deadline for S corporations because of
                  how these businesses are structured. Unlike C corporations, S
                  corporations are{' '}
                  <span style={{ fontWeight: 600 }}>pass-through entities</span>
                  . This means:
                </Typography>

                <Typography variant="body1" component="div">
                  <ul style={{ marginBottom: 24 }}>
                    <li style={{ marginBottom: 16 }}>
                      The corporation itself doesn’t generally pay income tax.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Instead, profits and losses “pass through” to
                      shareholders, who report them on their personal tax
                      returns.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      To do this, the corporation must issue{' '}
                      <Link
                        href="https://www.irs.gov/pub/irs-pdf/f1120ssk.pdf"
                        sx={{ color: '#0202ff' }}
                      >
                        Schedule K-1s
                      </Link>{' '}
                      to shareholders — and those K-1s come from Form 1120-S.
                    </li>
                  </ul>
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  If the IRS allowed S corporations to file at the same time as
                  individuals (April 15), shareholders wouldn’t have enough time
                  to report their share of income. That’s why the deadline falls
                  in <br />
                  <span style={{ fontWeight: 600 }}>mid-March</span>, giving
                  shareholders a buffer before their April 15 filing deadline.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                    scrollMarginTop: 83,
                  }}
                  id="exten"
                >
                  Extension Due Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your S corporation requires more time to{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-file Form 1120-S
                  </Link>
                  , you can get an{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    automatic{' '}
                    <Hidden smDown>
                      {' '}
                      <br />
                    </Hidden>{' '}
                    6-month
                  </span>{' '}
                  extension by{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 7004
                  </Link>{' '}
                  by the original due date. The extension offers you extra time
                  to file your return, but it does not extend the time to pay
                  any taxes owed.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            S Corporation Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Original Due Date
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Extension Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Calendar-Year S Corp
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            March 16, 2026
                          </TableCell>

                          <TableCell> September 15, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Fiscal-Year S Corp
                          </TableCell>
                          <TableCell sx={{ fontSize: 15 }}>
                            15th day of the 3rd month after tax year-end
                          </TableCell>

                          <TableCell>
                            {' '}
                            6 months after the original due date
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Example:</span> If you have
                  a fiscal year ending June 30, 2026, your Form 1120-S is due on
                  September 15, 2026. The extended filing date is March 15,
                  2027, when you file Form 7004.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Key Points to Remember{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    To receive an extension, file Form 7004 by the original due
                    date. This extension applies only to filing taxes, not to
                    payments.
                  </li>
                  <li>
                    You can avoid penalties and interest by paying any
                    corporate-level taxes due by the original due date.
                  </li>
                  <li>
                    You may still owe tax such as Built-in Gains Tax, Excess Net
                    Passive Income Tax, and any other corporate taxes that may
                    apply.
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note :</b> E-file Form 7004 with TaxZerone to receive your
                  automatic 6-month filing extension fast and securely.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="penalties"
                >
                  Penalties for Late Filing or Payment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS enforces strict late-filing penalties for S
                  corporations.
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 2 }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Late-Filing Penalty:
                    </span>{' '}
                    $245 per shareholder per month, up to 12 months, even if no
                    tax is owed.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Late-Payment Penalty:
                    </span>{' '}
                    If your S corporation owes taxes, 5% of unpaid tax per month
                    applies (first month: smaller of $510 or unpaid tax).
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Examples:
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 2 }}>
                  <li>
                    2 shareholders, 3 months late $245 × 2 × 3 = $1,470
                    (late-filing).
                  </li>
                  <li>
                    5 shareholders, 6 months late, plus $10,000 unpaid tax
                    late-filing + 5% of unpaid tax per month (late-payment).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Even if the S corporation has{' '}
                  <span style={{ fontWeight: 600 }}>no income</span> or{' '}
                  <span style={{ fontWeight: 600 }}>owes no tax</span>,
                  penalties still apply if the return is late.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="state"
                >
                  State Filing Deadlines
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In addition to the federal IRS deadline, many states require S
                  corporations to file separate state-level returns. Deadlines
                  vary:
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 2 }}>
                  <li>
                    Some states (e.g.,{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      California, New York
                    </span>
                    ) align with the federal due date.
                  </li>
                  <li>Others set their own filing deadlines.</li>
                  <li>
                    States may also have their own penalties for late filing,
                    separate from IRS penalties.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Always check with your state’s Department of Revenue or
                  Franchise Tax Board.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="best"
                >
                  Best Practices to Avoid Missing the Deadline
                </Typography>
                <Box component="ol" sx={{ '& li': { mb: 1.5 }, mb: 2 }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Use a tax calendar</span>{' '}
                    – Mark <span style={{ fontWeight: 600 }}>March 16</span>,
                    and <span style={{ fontWeight: 600 }}>September 15</span>
                    (if extended).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Prepare early</span> –
                    Gather financials right after year-end to avoid last-minute
                    rush.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Distribute K-1s early
                    </span>{' '}
                    – Shareholders need these forms to report their share of
                    income on personal tax returns by the April 15 deadline.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>File electronically</span>{' '}
                    – E-filing reduces errors and provides instant confirmation.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Set reminders</span> – Use
                    accounting software or digital reminders to track key dates.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Consult professionals
                    </span>{' '}
                    – If your business has complex operations, ensure you get
                    expert help early.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  Frequently Asked Questions (FAQs)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. What happens if my S corporation had no activity in 2025?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must still file Form 1120-S by March 16, 2026. The IRS
                  requires an annual return even if there was no income,
                  expense, or activity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Can I avoid penalties by filing late if I extend
                  shareholder 1040 returns?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. The corporation and the shareholder returns are separate.
                  Late 1120-S filing triggers corporate penalties regardless of
                  shareholder extensions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. If I file Form 7004, do I get more time to pay taxes?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. Form 7004 only extends the filing deadline. Any tax
                  liability is still due by the original deadline.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. How can shareholders be affected by a late 1120-S filing?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Shareholders may be forced to extend their personal returns if
                  K-1s are delayed, which can disrupt their tax planning and
                  refunds.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. Does every state follow the March deadline?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Not always. Some states have different due dates for corporate
                  returns. For example, New York requires an{' '}
                  <span style={{ fontWeight: 600 }}>
                    S corporation return (Form CT-3-S)
                  </span>
                  , and deadlines may differ.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  6. What if I miss both the original and extended deadlines?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss both deadlines, the IRS will impose the{' '}
                  <span style={{ fontWeight: 600 }}>
                    maximum late-filing penalty
                  </span>{' '}
                  (12 months × $245 × number of shareholders).{' '}
                  <span style={{ fontWeight: 600 }}>
                    If your S corporation owes taxes, a late-payment penalty of
                    5% per month may also apply.
                  </span>{' '}
                  Missing deadlines may also increase your risk of audit.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Don’t Risk Missing the Deadline
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 3,
              }}
            >
              The S corporation filing deadline is{' '}
              <span style={{ fontWeight: 600 }}> earlier than most expect</span>{' '}
              — and the penalties are step if you miss it. Preparing <br />
              ahead ensures your business stays compliant and your shareholders
              can file smoothly.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                // fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 600,
                color: '#333333',
                mb: 0.5,
              }}
            >
              Avoid costly penalties
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sduedates`}
              >
                E-file Form 1120-S
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120SDueDate;
